import {
  Component,
  OnDestroy,
  OnInit,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MenuItem, SelectItem } from '@openng/optimus-ui/api';
import { Editor } from '@openng/optimus-ui/editor';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { DELETE_LOG, PRINT } from 'src/app/constants/action-constants';
import {
  ALL_OPLOG_SEARCH_CONTAINER,
  OPALERTLOG_PRINT_CONTAINER,
} from 'src/app/constants/common.constants';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import {
  dynamicSort,
  filterDropdownResults,
  getDirtyValues,
  onlyUnique,
} from 'src/app/core/functions/common-functions';
import { TabService } from 'src/app/core/services/tab.service';
import { BreadCrumb } from 'src/app/model/common/bread-crumb';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { FilterCriteria } from 'src/app/model/common/filterCriteria';
import { Facility } from 'src/app/model/locations/facility';
import { AuthService } from 'src/app/services/auth.service';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { ContactTableService } from 'src/app/services/table/contact-table.service';
import { FacilityBuilderService } from 'src/app/services/builder/facility-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { OpAlertLog } from 'src/app/model/opAlertLogs/op-alert-log';
import { OpAlertLogEntryInput } from 'src/app/model/opAlertLogs/op-alert-log-entry-input';
import { OpAlertLogTableService } from 'src/app/services/table/op-alert-log-table.service';
import { OpAlertLogsBuilderService } from 'src/app/services/builder/op-alert-logs-builder.service';
import { OperationalMonitoringApiService } from 'src/app/services/api/operational-monitoring-api.service';
import { OpAlertLogInput } from 'src/app/model/opAlertLogs/op-alert-log-input';
import { CLAIMS } from 'src/app/constants/auth-constants';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
  AutoComplete,
} from '@openng/optimus-ui/autocomplete';
import { Location } from 'src/app/model/locations/location';
import { OpAlertLogEntry } from 'src/app/model/opAlertLogs/op-alert-log-entry';
import { Select, SelectChangeEvent } from '@openng/optimus-ui/select';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { PiValueDisplayComponent } from '../../components/pi-value-display/pi-value-display.component';
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@openng/optimus-ui/tabs';
import { Ripple } from '@openng/optimus-ui/ripple';
import { Panel } from '@openng/optimus-ui/panel';
import { DatePipe } from '@angular/common';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { ProgressSpinner } from '@openng/optimus-ui/progressspinner';
import { InputText } from '@openng/optimus-ui/inputtext';
import { ScrollPanel } from '@openng/optimus-ui/scrollpanel';
import { Dialog } from '@openng/optimus-ui/dialog';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { TranslatePipe } from '@ngx-translate/core';
import { LogEntryComponent } from 'src/app/core/components/log-entry.component';

@Component({
  selector: 'app-oplogs-detail-container',
  templateUrl: './oplogs-detail-container.component.html',
  styleUrls: ['./oplogs-detail-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    FormsModule,
    ReactiveFormsModule,
    ButtonDirective,
    PiValueDisplayComponent,
    Select,
    Tabs,
    TabList,
    Ripple,
    Tab,
    TabPanels,
    TabPanel,
    Panel,
    AutoComplete,
    Tooltip,
    ProgressSpinner,
    InputText,
    ScrollPanel,
    Dialog,
    DatePicker,
    Editor,
    DatePipe,
    TranslatePipe,
    LogEntryComponent,
  ],
})
export class OplogsDetailContainerComponent
  extends DetailsContainer
  implements OnInit, OnDestroy
{
  protected _breadCrumbService = inject(BreadCrumbBuilderService);
  protected _fb = inject(UntypedFormBuilder);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _opMonitoringApi = inject(OperationalMonitoringApiService);
  protected _authApi = inject(AuthApiService);
  protected _opAlertLogBuilder = inject(OpAlertLogsBuilderService);
  protected _contactTable = inject(ContactTableService);
  protected _activeRoute = inject(ActivatedRoute);
  protected _facilityBuilder = inject(FacilityBuilderService);
  protected _auth = inject(AuthService);
  protected _opLogTableService = inject(OpAlertLogTableService);

  readonly editor = viewChild(Editor);

  tabs: any[] = [];
  opLog: OpAlertLog | null = null;
  opLogBreadCrumb?: BreadCrumb;
  types: SelectItem[] = [];
  showSeverity = false;
  showNotam = false;
  isEditting = false;

  facilities: Facility[] = [];
  facilityTypes: any[] = [];
  filteredFacilityTypes: any[] = [];
  facilityNames: any[] = [];
  filteredFacilityNames: any[] = [];
  facilityIds: any[] = [];
  filteredFacilityIds: any[] = [];
  filteredBusinessUnits: any[] = [];
  facilitiesLoading: boolean = true;

  contacts: any[] = [];
  filteredContacts: any[] = [];
  showEntryDialog = false;

  addEntryForm: UntypedFormGroup;
  addingAction = false;
  saving = false;
  forwardOptions: MenuItem[] = [];
  primaryContact: any;
  primaryContacts: MenuItem[] = [];

  actions: SelectItem[] = [];

  quillModules: any;
  sub?: Subscription;

  canEdit = false;
  canEditOwnEntry = false;
  canEditDirectReportEntries = false;

  canDelete = false;
  canDeleteOwnEntry = false;
  canDeleteDirectReportEntries = false;

  canAddEntry = false;
  canAddOwnEntry = false;
  canAddDirectReportEntries = false;
  allowedToModifyIds: string[] = [];

  deleteMsg: string = '';
  deleteEntryMsg: string = '';
  deleteEntryHeader: string = '';
  okBtnLabel: string = '';
  cancelBtnLabel: string = '';
  deleteHeader: string = '';
  isAlarm = false;
  showAcknowledged = false;

  displayEmailDialog = false;
  emailDialogHeader: string = '';
  selectedAction: any = null;
  showGasReleasePopupMessage = false;
  showCalloutPopupMessage = false;
  tabIndex = 0;
  breadCrumbSub?: Subscription;
  activeTabChangedSub?: Subscription;

  constructor() {
    super();
    this.addEntryForm = this._fb.group({
      statusUpdate: [null, Validators.required],
      id: null,
      entryTime: null,
    });
  }

  ngOnInit() {
    this.setupTabClosingSubscription();
    this.setupBreadCrumbSub();
    this.autoSelect = true;

    this.initializeOpLog();

    this.loadFacilities();
  }

  refresh() {
    this.loading = true;
    this.ngOnInit(); //reloading page
  }

  setupBreadCrumbSub(): void {
    this.breadCrumbSub =
      this._breadCrumbService.breadCrumbSelectedEvent.subscribe(
        (breadCrumb) => {
          this._breadCrumbService.removeBreadCrumb(this.opLogBreadCrumb);
          const tab = this._tabService.buildNewTab(
            ALL_OPLOG_SEARCH_CONTAINER,
            true
          );
          this._tabService.setMobileTab(tab);
        }
      );
  }

  initializeOpLog() {
    const id = this.id ? this.id : this._activeRoute.snapshot.params['id'];
    const deleteMsg$ = this._translateService.get(
      'OPMONITORING.MESSAGES.CONFIRMATION.DELETE_OPLOG'
    );
    const deleteEntryMsg$ = this._translateService.get(
      'COMMON.MESSAGES.CONFIRMATION.DELETE_ENTRY'
    );
    const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
    const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
    const header$ = this._translateService.get(
      'OPMONITORING.MESSAGES.HEADERS.DELETE_OP_ALERTLOG'
    );
    const deleteEntryHeader$ = this._translateService.get(
      'COMMON.MESSAGES.HEADERS.DELETE_ENTRY'
    );
    forkJoin([
      deleteMsg$,
      ok$,
      cancel$,
      header$,
      deleteEntryMsg$,
      deleteEntryHeader$,
    ]).subscribe((messages) => {
      this.deleteMsg = messages[0];
      this.okBtnLabel = messages[1];
      this.cancelBtnLabel = messages[2];
      this.deleteHeader = messages[3];
      this.deleteEntryMsg = messages[4];
      this.deleteEntryHeader = messages[5];
    });

    // We should always have an id here, but handle just in case
    if (!id) {
      this._logAndMessage.errorLogOnly(
        'Could not find op alert log with ID: ' + id
      );

      if (this._deviceService.isMobile()) {
        const tab = this._tabService.buildNewTab(
          ALL_OPLOG_SEARCH_CONTAINER,
          true
        );
        this._tabService.setMobileTab(tab);
      } else {
        const index = this._tabService.getActiveIndex();
        this._tabService.closeTab(index);
      }
    } else {
      // Otherwise, grab the log from the db via the id
      this._opMonitoringApi.getOpAlertLog(id).subscribe({
        next: (log) => {
          if (log) {
            this.opLog = log;
            this.opLog.Facility = this._facilityBuilder.buildFacility(
              this.opLog.Facility,
              []
            );
            this.initialize();
          } else {
            // nothing found, go back to search.
            this._logAndMessage.errorLogOnly(
              'Could not find op alert log with ID: ' + id
            );

            if (this._deviceService.isMobile()) {
              const tab = this._tabService.buildNewTab(
                ALL_OPLOG_SEARCH_CONTAINER,
                true
              );
              this._tabService.setMobileTab(tab);
            } else {
              this.loading = false;
              const index = this._tabService.getActiveIndex();
              this._tabService.closeTab(index);
            }
          }
        },
        error: (error) => {
          console.error(error);
          // nothing found, go back to search or previous tab
          this._logAndMessage.errorLogOnly(
            'Error finding op alert log with ID: ' + id
          );
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'MONLOG.MESSAGES.ERROR.LOAD_MON_LOG',
          });
          if (this._opLogTableService.getLastScreen() === 'all') {
            if (this._deviceService.isMobile()) {
              const tab = this._tabService.buildNewTab(
                ALL_OPLOG_SEARCH_CONTAINER,
                true
              );
              this._tabService.setMobileTab(tab);
            } else {
              const index = this._tabService.getActiveIndex();
              this._tabService.closeTab(index);
            }
          } else {
            const index = this._tabService.getActiveIndex();
            this._tabService.closeTab(index);
          }
        },
        complete: () => {
          this.doCheckComplete = false;
        },
      });
    }
  }

  initialize() {
    if (!this.opLog || (this.opLog && this.opLog.id === null)) {
      this.isEditting = true;
    }

    if (!this._deviceService.isMobile()) {
      this.screenName = this.opLog?.opLogId + ' (Field Notification)';
      const tab = this._tabService.getTabById(this.opLog!.id);
      this._tabService.updateTabLabel(tab!, this.screenName);
    }

    this.buildForm();
    this.updateForm();
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.FIELD_OPERATIONS.OPERATIONAL_ALERTS.EDIT_OPERATIONAL_ALERT,
    ]);
    this.canEditOwnEntry = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.FIELD_OPERATIONS.OPERATIONAL_ALERTS.ENTRIES
        .EDIT_OWN_OPERATIONAL_ALERT_ENTRY,
    ]);
    this.canEditDirectReportEntries =
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.FIELD_OPERATIONS.OPERATIONAL_ALERTS.ENTRIES
          .EDIT_DIRECT_REPORT_OPERATIONAL_ALERT_ENTRY,
      ]);

    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.FIELD_OPERATIONS.OPERATIONAL_ALERTS.DELETE_OPERATIONAL_ALERT,
    ]);
    this.canDeleteOwnEntry = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.FIELD_OPERATIONS.OPERATIONAL_ALERTS.ENTRIES
        .DELETE_OWN_OPERATIONAL_ALERT_ENTRY,
    ]);
    this.canDeleteDirectReportEntries =
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.FIELD_OPERATIONS.OPERATIONAL_ALERTS.ENTRIES
          .DELETE_DIRECT_REPORT_OPERATIONAL_ALERT_ENTRY,
      ]);

    this.canAddEntry = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.FIELD_OPERATIONS.OPERATIONAL_ALERTS.ENTRIES
        .ADD_OPERATIONAL_ALERT_ENTRY,
    ]);
    this.canAddOwnEntry = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.FIELD_OPERATIONS.OPERATIONAL_ALERTS.ENTRIES
        .ADD_OWN_OPERATIONAL_ALERT_ENTRY,
    ]);
    this.canAddDirectReportEntries =
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.FIELD_OPERATIONS.OPERATIONAL_ALERTS.ENTRIES
          .ADD_DIRECT_REPORT_OPERATIONAL_ALERT_ENTRY,
      ]);

    if (this.opLog?.Contact) {
      this._contactApi
        .queryForWhoReportsToMe(this._auth.getEmail()!)
        .pipe(take(1))
        .subscribe((users) => {
          this.allowedToModifyIds = users.map((c: any) => c.id);
          if (
            this.allowedToModifyIds.some((i) => i === this.opLog?.Contact.id)
          ) {
            if (this.canEditDirectReportEntries) {
              this.canEdit = true;
            }

            if (this.canAddDirectReportEntries) {
              this.canAddEntry = true;
            }

            if (this.canDeleteDirectReportEntries) {
              this.canDelete = true;
            }
          }
        });
    }

    if (this.opLog) {
      this.showAcknowledged = this.opLog.Contact != null;
    }

    this.loadDropDownData();

    if (
      this._deviceService.isMobile() &&
      (!this._opLogTableService.getLastScreen() ||
        this._opLogTableService.getLastScreen() === 'all')
    ) {
      this._translateService
        .get('OPMONITORING.SCREEN.ALL_OPERATIONAL_LOGS')
        .subscribe((label) => {
          this._breadCrumbService.resetAndAddBreadCrumb(
            new BreadCrumb(label, null, true)
          );
          if (this.opLog) {
            this.opLogBreadCrumb = new BreadCrumb(
              this.opLog.opLogId!,
              null,
              false
            );
            this._breadCrumbService.addBreadCrumb(this.opLogBreadCrumb);
          }
        });
    }

    this.updateActionsList();
    this.loading = false;
  }

  loadFacilities() {
    this.facilitiesLoading = true;
    this._facilityApi.getFacilities().subscribe((data) => {
      this.facilities = [...data];
      this.facilities = this.facilities.filter(
        (facility) => facility.hidden == false
      );
      this.facilitiesLoading = false;
    });
  }

  facilityTypeSearch($event: AutoCompleteCompleteEvent) {
    if (this.form) {
      const detailGroup = this.form
        ?.get('OpAlertLog')
        ?.get('OpAlertLogLocation') as UntypedFormGroup;
      const state = detailGroup.controls['state'].value;
      const countyContainer = detailGroup.controls['county'].value;
      const townshipContainer = detailGroup.controls['township'].value;
      const businessUnit = this.form.controls['businessUnit'].value;

      const queryArray: FilterCriteria[] = [];
      if (state) {
        queryArray.push(new FilterCriteria('state', state, 'include'));
      }
      if (countyContainer && countyContainer.county) {
        queryArray.push(
          new FilterCriteria('county', countyContainer.county, 'include')
        );
      }
      if (townshipContainer && townshipContainer.township) {
        queryArray.push(
          new FilterCriteria('township', townshipContainer.township, 'include')
        );
      }
      if (businessUnit) {
        queryArray.push(
          new FilterCriteria(
            'businessUnit.id',
            businessUnit.id,
            'array-includes'
          )
        );
      }
      if ($event.query !== '') {
        queryArray.push(
          new FilterCriteria('facilityType', $event.query, 'include')
        );
      }

      if (this.facilities) {
        this.filteredFacilityTypes = filterDropdownResults(
          this.facilities,
          queryArray
        )
          .map((ft: any) => ft.facilityType)
          .filter(onlyUnique);
        this.filteredFacilityTypes.sort();
      }
    }
  }

  facilityNameSearch($event: AutoCompleteCompleteEvent) {
    const detailGroup = this.form
      ?.get('OpAlertLog')
      ?.get('OpAlertLogLocation') as UntypedFormGroup;
    const facilityGroup = this.form
      ?.get('OpAlertLog')
      ?.get('OpAlertLogFacility') as UntypedFormGroup;
    const state = detailGroup.controls['state'].value;
    const countyContainer = detailGroup.controls['county'].value;
    const townshipContainer = detailGroup.controls['township'].value;
    const facilityType = facilityGroup.controls['facilityType'].value;
    const businessUnit = this.form?.controls['businessUnit'].value;

    const queryArray: FilterCriteria[] = [];
    if (state) {
      queryArray.push(new FilterCriteria('state', state, 'include'));
    }
    if (countyContainer && countyContainer.county) {
      queryArray.push(
        new FilterCriteria('county', countyContainer.county, 'include')
      );
    }
    if (townshipContainer && townshipContainer.township) {
      queryArray.push(
        new FilterCriteria('township', townshipContainer.township, 'include')
      );
    }
    if (facilityType) {
      queryArray.push(
        new FilterCriteria('facilityType', facilityType, 'include')
      );
    }
    if (businessUnit) {
      queryArray.push(
        new FilterCriteria('businessUnit.id', businessUnit.id, 'array-includes')
      );
    }
    if ($event.query !== '') {
      queryArray.push(
        new FilterCriteria('facilityName', $event.query, 'include')
      );
    }

    let facilityNames = filterDropdownResults(this.facilities, queryArray);
    facilityNames.sort(dynamicSort('facilityName', 1));
    this.filteredFacilityNames = facilityNames;
    if (this.filteredFacilityNames.length === 1) {
      facilityGroup.patchValue({ facilityName: this.filteredFacilityNames[0] });
      this.facilityNameSelected(this.filteredFacilityNames[0]);
    }
  }

  facilityNameSelected($event: AutoCompleteSelectEvent | any) {
    this._facilityApi.getFacility($event.id).subscribe({
      next: (facility) => {
        if (facility.locations.length > 0) {
          const detailGroup = this.form
            ?.get('OpAlertLog')
            ?.get('OpAlertLogLocation') as UntypedFormGroup;

          let location = null;
          if (
            !isNullOrUndefined(detailGroup) &&
            !isNullOrUndefined(detailGroup.controls['township']) &&
            !isNullOrUndefined(detailGroup.controls['township'].value) &&
            !isNullOrUndefined(detailGroup.controls['township'].value.township)
          ) {
            const existingLoc = detailGroup.controls['township'].value;
            location = facility.locations.find(
              (loc: Location) => loc.township === existingLoc.township
            )!;
          } else {
            location = facility.locations[0];
          }

          detailGroup.patchValue({
            locationId: location.id,
            state: location.state,
            county: { county: location.county, state: location.state },
            township: { township: location.township },
          });
          detailGroup.controls['state'].markAsDirty();
          detailGroup.controls['county'].markAsDirty();
          detailGroup.controls['township'].markAsDirty();
        }
        const group = this.form
          ?.get('OpAlertLog')
          ?.get('OpAlertLogFacility') as UntypedFormGroup;
        group.patchValue({
          facilityUid: $event,
          facilityType: facility.FacilityType?.name,
          aor: facility.AORs[0].name,
          facilityId: facility.id,
        });
        group.controls['facilityUid'].markAsDirty();
        group.controls['facilityType'].markAsDirty();
        group.controls['aor'].markAsDirty();
        group.controls['facilityId'].markAsDirty();

        // get the first business unit and set
        const businessUnit = facility.businessUnit;
        if (businessUnit != null) {
          this.form?.patchValue({
            businessUnit: businessUnit,
          });
          this.form?.controls['businessUnit'].markAsDirty();
        }
      },
      error: (error) => {
        console.error('Facility that was selected was not found in database!');
        console.error(error);
        this._logAndMessage.errorAlert(
          'Facility not found! Selected Facility was not found in the database. Re-open the dropdown and try again.'
        );
        const group = this.form
          ?.get('OpAlertLog')
          ?.get('OpAlertLogFacility') as UntypedFormGroup;
        group.patchValue({
          facilityName: null,
        });
      },
    });
  }

  facilityIdSearch($event: AutoCompleteCompleteEvent) {
    const detailGroup = this.form
      ?.get('OpAlertLog')
      ?.get('OpAlertLogLocation') as UntypedFormGroup;
    const facilityGroup = this.form
      ?.get('OpAlertLog')
      ?.get('OpAlertLogFacility') as UntypedFormGroup;
    const state = detailGroup.controls['state'].value;
    const countyContainer = detailGroup.controls['county'].value;
    const townshipContainer = detailGroup.controls['township'].value;
    const facilityType = facilityGroup.controls['facilityType'].value;
    const facilityNameContainer = facilityGroup.controls['facilityName'].value;
    const businessUnit = this.form?.controls['businessUnit'].value;

    const queryArray: FilterCriteria[] = [];
    if (state) {
      queryArray.push(new FilterCriteria('state', state, 'include'));
    }
    if (countyContainer && countyContainer.county) {
      queryArray.push(
        new FilterCriteria('county', countyContainer.county, 'include')
      );
    }

    if (townshipContainer && townshipContainer.township) {
      queryArray.push(
        new FilterCriteria('township', townshipContainer.township, 'include')
      );
    }

    if (facilityType) {
      queryArray.push(
        new FilterCriteria('facilityType', facilityType, 'include')
      );
    }

    if (facilityNameContainer && facilityNameContainer.facilityName) {
      queryArray.push(
        new FilterCriteria(
          'facilityName',
          facilityNameContainer.facilityName,
          'include'
        )
      );
    }

    if (businessUnit) {
      queryArray.push(
        new FilterCriteria('businessUnit.id', businessUnit.id, 'array-includes')
      );
    }

    if ($event.query !== '') {
      queryArray.push(
        new FilterCriteria('facilityUid', $event.query, 'include')
      );
    }

    this.filteredFacilityIds = filterDropdownResults(
      this.facilities,
      queryArray
    );
    this.filteredFacilityIds.sort();
    if (this.filteredFacilityIds.length === 1) {
      facilityGroup.patchValue({ facilityUid: this.filteredFacilityIds[0] });
      this.facilityIdSelected(this.filteredFacilityIds[0]);
    }
  }

  facilityIdSelected($event: AutoCompleteSelectEvent | any) {
    this._facilityApi.getFacility($event.id).subscribe({
      next: (facility) => {
        if (facility.locations.length > 0) {
          const detailGroup = this.form
            ?.get('OpAlertLog')
            ?.get('OpAlertLogLocation') as UntypedFormGroup;

          let location = null;
          if (
            !isNullOrUndefined(detailGroup) &&
            !isNullOrUndefined(detailGroup.controls['township']) &&
            !isNullOrUndefined(detailGroup.controls['township'].value) &&
            !isNullOrUndefined(detailGroup.controls['township'].value.township)
          ) {
            const existingLoc = detailGroup.controls['township'].value;
            location = facility.locations.find(
              (loc: Location) => loc.township === existingLoc.township
            )!;
          } else {
            location = facility.locations[0];
          }

          detailGroup.patchValue({
            locationId: location.id,
            state: location.state,
            county: { county: location.county, state: location.state },
            township: { township: location.township },
          });
          detailGroup.controls['state'].markAsDirty();
          detailGroup.controls['county'].markAsDirty();
          detailGroup.controls['township'].markAsDirty();
        }
        const group = this.form
          ?.get('OpAlertLog')
          ?.get('OpAlertLogFacility') as UntypedFormGroup;
        group.patchValue({
          facilityName: $event,
          facilityType: facility.FacilityType?.name,
          aor: facility.AORs[0].name,
          facilityId: facility.id,
        });

        group.controls['facilityName'].markAsDirty();
        group.controls['facilityType'].markAsDirty();
        group.controls['aor'].markAsDirty();
        group.controls['facilityId'].markAsDirty();

        // get the first business unit and set
        const businessUnit = facility.businessUnit;
        if (businessUnit != null) {
          this.form?.patchValue({
            businessUnit: businessUnit,
          });
          this.form?.controls['businessUnit'].markAsDirty();
        }
      },
      error: (error) => {
        console.error('Facility that was selected was not found in database!');
        console.error(error);
        this._logAndMessage.errorAlert(
          'Facility not found! Selected Facility was not found in the database. Re-open the dropdown and try again.'
        );
        const group = this.form
          ?.get('OpAlertLog')
          ?.get('OpAlertLogFacility') as UntypedFormGroup;
        group.patchValue({
          facilityUid: null,
        });
      },
    });
  }

  businessUnitSearch($event: AutoCompleteCompleteEvent) {
    const detailGroup = this.form
      ?.get('OpAlertLog')
      ?.get('OpAlertLogLocation') as UntypedFormGroup;
    const facilityGroup = this.form
      ?.get('OpAlertLog')
      ?.get('OpAlertLogFacility') as UntypedFormGroup;
    const state = detailGroup.controls['state'].value;
    const countyContainer = detailGroup.controls['county'].value;
    const townshipContainer = detailGroup.controls['township'].value;
    const facilityType = facilityGroup.controls['facilityType'].value;
    const facilityNameContainer = facilityGroup.controls['facilityName'].value;

    const queryArray: FilterCriteria[] = [];
    if (state) {
      queryArray.push(new FilterCriteria('state', state, 'include'));
    }
    if (countyContainer && countyContainer.county) {
      queryArray.push(
        new FilterCriteria('county', countyContainer.county, 'include')
      );
    }
    if (townshipContainer && townshipContainer.township) {
      queryArray.push(
        new FilterCriteria('township', townshipContainer.township, 'include')
      );
    }

    if (facilityType) {
      queryArray.push(
        new FilterCriteria('facilityType', facilityType, 'include')
      );
    }

    if (facilityNameContainer && facilityNameContainer.facilityName) {
      queryArray.push(
        new FilterCriteria(
          'facilityName',
          facilityNameContainer.facilityName,
          'include'
        )
      );
    }

    if ($event.query !== '') {
      queryArray.push(
        new FilterCriteria('businessUnits', $event.query, 'include')
      );
    }

    let filteredFacilities = filterDropdownResults(this.facilities, queryArray);
    this.filteredBusinessUnits = filteredFacilities
      .map((f) => f.businessUnits)
      .reduce((acc, val) => acc.concat(val), [])
      .filter(
        (val: Facility, index: number, self: Facility[]) =>
          self.map((e) => e.id).indexOf(val.id) == index
      );

    // Ensure that we only show business units that the user has access to
    // even if one of their facilities has a BU that they don't have
    const userBusinessUnitNames = this._authApi
      .getUserBusinessUnits()
      .map((userBu) => userBu.name);
    this.filteredBusinessUnits = this.filteredBusinessUnits.filter((filterBu) =>
      userBusinessUnitNames.includes(filterBu.name)
    );

    this.filteredBusinessUnits.sort();
    if (this.filteredBusinessUnits.length == 1) {
      this.form?.patchValue({ businessUnit: this.filteredBusinessUnits[0] });
    }
  }

  clearScreen() {
    this.buildForm();
  }

  saveOperatorLog(markLogAsDeleted: boolean) {
    this.saving = true;
    this.isEditting = false;
    const dv = getDirtyValues(this.form);

    const updateStatement = this._opAlertLogBuilder.buildUpdateStatement(
      this.opLog!,
      dv,
      markLogAsDeleted
    );
    updateStatement.updatedBy = this._auth.getUserName();

    if (this.opLog?.id) {
      this.updateOperatorLog(updateStatement);
    } else {
      this.opLog!.opLogId = undefined;
      this._logAndMessage.errorAlert(
        'Cannot create new Field Notification! No Field Notification ID found.'
      );
    }
  }

  saveOperatorLogEntry(markLogAsDeleted: boolean) {
    this.saving = true;
    this.isEditting = false;
    const dv = getDirtyValues(this.form);

    const updateStatement = this._opAlertLogBuilder.buildUpdateStatement(
      this.opLog!,
      dv,
      markLogAsDeleted
    );
    updateStatement.updatedBy = this._auth.getUserName();

    this.updateOperatorLogEntry(updateStatement['OpAlertLogEntry']);
  }

  updateOperatorLog(updateStatement: any) {
    if (!this.canEdit) {
      this._logAndMessage.errorAlert(
        'Cannot update Field Notification! Logged in user does not have appropriate claim.'
      );
      return;
    }

    let inputObject: OpAlertLogInput = {
      id: updateStatement.id,
      opLogId: updateStatement.opLogId,
      opLogStatus: updateStatement.opLogStatus,
      facilityId: updateStatement.facilityId,
      state: updateStatement.state,
      county: updateStatement.county,
      township: updateStatement.township,
      businessUnitId: updateStatement.businessUnitId,
      updatedBy: updateStatement.updatedBy,
      markAsDeleted: updateStatement.markAsDeleted,
    };

    this._opMonitoringApi.updateOpAlertLog(this.opLog!, inputObject).subscribe({
      next: (log) => {
        this.handleLogSave();
      },
      error: (error) => {
        console.error(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'MONLOG.MESSAGES.ERROR.UPDATE_LOG',
        });
        this.saving = false;
      },
      complete: () => {
        this.saving = false;
      },
    });
  }

  updateOperatorLogEntry(updateStatement: any) {
    if (!this.canEdit) {
      if (
        (!this.canEdit && !this.canEditOwnEntry) ||
        (this.canEditOwnEntry && !this.isOwnLog())
      ) {
        this._logAndMessage.errorAlert(
          'Cannot update Field Notification entry! Logged in user does not have appropriate claim.'
        );
        return;
      }
    }

    let inputObject: OpAlertLogEntryInput = {
      id: updateStatement.id,
      opAlertLogId: updateStatement.opAlertLogId,
      time: Math.round(
        (typeof updateStatement.time !== 'string'
          ? updateStatement.time.getTime()
          : new Date(updateStatement.time).getTime()) / 1000
      ),
      entry: updateStatement.entry,
      markAsDeleted: updateStatement.markAsDeleted,
    };

    if (updateStatement.dirtyStatus == DirtyStatus.NEW) {
      this._opMonitoringApi.createOpAlertLogEntry(inputObject).subscribe({
        next: (entry) => {
          this.handleLogSave();
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'MONLOG.MESSAGES.ERROR.CREATE_LOG_ENTRY',
          });
          this.saving = false;
        },
        complete: () => {
          this.saving = false;
        },
      });
    } else {
      this._opMonitoringApi.updateOpAlertLogEntry(inputObject).subscribe({
        next: (entry) => {
          this.handleLogSave();
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'MONLOG.MESSAGES.ERROR.UPDATE_LOG_ENTRY',
          });
          this.saving = false;
        },
      });
    }
  }

  handleLogSave() {
    this._logAndMessage.translateToSuccessMessage({
      bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
      headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
    });

    this._opMonitoringApi.getOpAlertLog(this.opLog!.id).subscribe((log) => {
      if (log) {
        this.opLog = log;
        this.opLog.Facility = this._facilityBuilder.buildFacility(
          this.opLog.Facility,
          []
        );
        this.initialize();
      } else {
        // nothing found, go back to search.
        this._logAndMessage.errorLogOnly(
          'Could not find op alert log with ID: ' + this.opLog?.id
        );

        if (this._deviceService.isMobile()) {
          const tab = this._tabService.buildNewTab(
            ALL_OPLOG_SEARCH_CONTAINER,
            true
          );
          this._tabService.setMobileTab(tab);
        } else {
          const index = this._tabService.getActiveIndex();
          this._tabService.closeTab(index);
        }
      }
      this.saving = false;
    });

    this.form?.disable();
    this.form?.markAsPristine();
    this.updateActionsList();

    this.changesEvent.emit({
      isDirty: false,
      index: this.index(),
      id: this.id,
    });

    setTimeout(() => {
      this.updateForm();
    }, 2000);
  }

  buildForm() {
    this.form = this._fb.group({
      businessUnit: [null, Validators.required],
      opLogStatus: null,
      OpAlertLog: this._fb.group({
        OpAlertLogLocation: this._fb.group({
          state: null,
          county: null,
          township: null,
        }),
        OpAlertLogFacility: this._fb.group({
          facilityName: null,
          facilityType: null,
          facilityUid: null,
          aor: [{ value: null, disabled: true }],
          pipeline: null,
          facilityId: null,
          businessUnit: null,
        }),
      }),
    });

    this.valueSub = this.form.valueChanges.subscribe((change) => {
      if (this.form?.dirty) {
        this.changesEvent.emit({
          isDirty: true,
          index: this.index(),
          id: this.id,
        });
      }
    });
  }

  updateForm() {
    this.form?.patchValue({
      opLogStatus: this.opLog?.opLogStatus,
    });

    if (this.opLog) {
      if (this.opLog.Facility) {
        this.form
          ?.get('OpAlertLog')
          ?.get('OpAlertLogFacility')
          ?.patchValue({
            // facilityName and facilityUid are objects and not straight fields.  This is because of the selection/ auto update cases
            // these drop downs use.
            facilityName: { facilityName: this.opLog.Facility.facilityName },
            facilityUid: { facilityUid: this.opLog.Facility.facilityUid },
            facilityType: this.opLog?.Facility?.FacilityType?.name,
            aor: this.opLog.Facility.AORs[0].name,
            businessUnit: this.opLog.BusinessUnit,
          });
      }

      if (this.opLog) {
        this.form
          ?.get('OpAlertLog')
          ?.get('OpAlertLogLocation')
          ?.patchValue({
            state: this.opLog.state,
            // county and township are objects and not straight fields.  This is because of the selection/ auto update cases
            // these drop downs use.
            county: this.opLog.county
              ? { county: this.opLog.county, state: this.opLog.state }
              : null,
            township: this.opLog.township
              ? { township: this.opLog.township }
              : null,
          });
      }

      if (this.opLog.id === null) {
        const callGroup = this.form?.get('OpAlertLog') as UntypedFormGroup;
        this.markFormGroupDirty(callGroup);
      }

      if (this.opLog.OpAlertLogAlarms.length > 0) {
        this.isAlarm = true;
      }
    }
    if (!this.isEditting) {
      this.form?.disable();
    }
  }

  private markFormGroupDirty(formGroup: UntypedFormGroup) {
    (<any>Object).values(formGroup.controls).forEach((control: any) => {
      control.markAsDirty();

      if (control.controls) {
        this.markFormGroupDirty(control);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.breadCrumbSub) {
      this.breadCrumbSub.unsubscribe();
    }

    if (this.sub) {
      this.sub.unsubscribe();
    }

    if (this.valueSub) {
      this.valueSub.unsubscribe();
    }

    if (this.tabClosingSub) {
      this.tabClosingSub.unsubscribe();
    }

    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  showEntryForm() {
    this.showEntryDialog = true;
  }

  cancelEntryForm() {
    this.addEntryForm.reset();
    this.showEntryDialog = false;
    this.addingAction = false;
  }

  private processOpAlertLogEntry(raw: any) {
    if (!raw.id) {
      this.opLog!.OpAlertLogEntries = [
        ...this.opLog!.OpAlertLogEntries,
        this._opAlertLogBuilder.buildNewOpAlertLogEntry(raw, this.opLog!),
      ];
    } else {
      // need to re-open log.
      const actions = this.opLog!.OpAlertLogEntries.filter(
        (a) => a.id === raw.id
      );
      const action = actions.length > 0 ? actions[0] : null;

      if (action) {
        action.opAlertLogId = this.opLog!.id;
        action.entry = raw.statusUpdate;
        action.time = raw.entryTime ? raw.entryTime : new Date();
        action.dirtyStatus = DirtyStatus.UPDATED;
        action.updatedAt = new Date();
        action.markAsDeleted = false;
        action.createdBy = this._auth.getUserName();
        action.createdAt = raw.entryTime ? raw.entryTime : new Date();
        action.user = this._auth.getUserName();
      }
    }
  }

  addEntryToOperatorLog() {
    if (!this.canAddEntry) {
      if (!this.canAddOwnEntry || (this.canAddOwnEntry && !this.isOwnLog())) {
        this._logAndMessage.errorAlert(
          'Cannot add Field Notification entry! Logged in user does not have appropriate claim.'
        );
        return;
      }
    }

    const raw = this.addEntryForm.getRawValue();
    this.processOpAlertLogEntry(raw);

    this.saveOperatorLogEntry(false);
    this.addEntryForm.reset();
    this.showEntryDialog = false;
  }

  editEntry($event: OpAlertLogEntry, isAction = false) {
    if (!this.canEdit) {
      if (!this.canEditOwnEntry || (this.canEditOwnEntry && !this.isOwnLog())) {
        this._logAndMessage.errorAlert(
          'Cannot edit Field Notification entry! Logged in user does not have appropriate claim.'
        );
        return;
      }
    }
    this.addEntryForm.patchValue({
      id: $event.id,
      statusUpdate: $event.entry,
      entryTime: $event.time,
    });
    this.addingAction = isAction;
    this.showEntryDialog = true;
  }

  deleteEntry($event: OpAlertLogEntry, isAction = false) {
    if (!this.canDelete) {
      if (
        !this.canDeleteOwnEntry ||
        (this.canDeleteOwnEntry && !this.isOwnLog())
      ) {
        this._logAndMessage.errorAlert(
          'Cannot delete Field Notification entry! Logged in user does not have appropriate claim.'
        );
        return;
      }
    }

    this._confirmationService.confirm({
      header: this.deleteEntryHeader,
      icon: 'fa fa-question-circle',
      acceptVisible: true,
      rejectVisible: true,
      acceptLabel: this.okBtnLabel,
      rejectLabel: this.cancelBtnLabel,
      message: this.deleteEntryMsg,
      accept: () => {
        if (isAction) {
          this.opLog?.OpAlertLogEntries.filter(
            (a) => a.id === $event.id
          ).forEach((a) => {
            a.deletedAt = new Date();
            a.dirtyStatus = DirtyStatus.DELETED;
            a.markAsDeleted = true;
            a.opAlertLogId = this.opLog?.id;
          });
        } else {
          this.opLog?.OpAlertLogEntries.filter(
            (a) => a.id === $event.id
          ).forEach((a) => {
            a.deletedAt = new Date();
            a.dirtyStatus = DirtyStatus.DELETED;
            a.markAsDeleted = true;
            a.opAlertLogId = this.opLog?.id;
          });
        }
        this.saveOperatorLogEntry(true);
      },
      reject: () => {
        this.saving = false;
      },
    });
  }

  updateActionsList() {
    const selectAction$ = this._translateService.get(
      'OPMONITORING.LABEL.SELECT_ACTION'
    );
    const deleteLog$ = this._translateService.get(
      'OPMONITORING.LABEL.DELETE_LOG'
    );
    const print$ = this._translateService.get('COMMON.LABEL.PRINT');

    let select,
      del,
      print = null;
    forkJoin([selectAction$, deleteLog$, print$])
      .pipe(take(1))
      .subscribe((messages) => {
        select = messages[0];
        del = messages[1];
        print = messages[2];

        this.actions = [
          { label: select, value: null },
          { label: print, value: PRINT },
        ];

        if (this.canDelete) {
          this.actions.push({ label: del, value: DELETE_LOG });
        }

        // default actions here: Send to Day Crew, etc.
        this.actions = [...this.actions];
      });
  }

  actionEvent($event: SelectChangeEvent, object?: Select) {
    if ($event.value) {
      this.selectedAction = null;
      if ($event.value) {
        if ($event.value === DELETE_LOG) {
          //#######################################################################
          this.deleteOpAlertLog();
          this.ngOnInit(); //reloading page
          //#######################################################################
        } else if ($event.value === PRINT) {
          //#######################################################################
          const tab = this._tabService.buildNewTab(
            OPALERTLOG_PRINT_CONTAINER,
            true,
            undefined,
            this.opLog!.id
          );
          if (this._deviceService.isMobile()) {
            this._tabService.setMobileTab(tab);
          } else {
            this._tabService.openTab(tab);
          }
        }
        //#######################################################################
      }

      if (object) {
        object.clear();
      }
    }
  }

  deleteOpAlertLog(): void {
    if (!this.canDelete) {
      this._logAndMessage.errorAlert(
        'Cannot delete Field Notification! Logged in user does not have appropriate claim.'
      );
      return;
    }

    this._confirmationService.confirm({
      header: this.deleteHeader,
      icon: 'fa fa-question-circle',
      acceptVisible: true,
      rejectVisible: true,
      acceptLabel: this.okBtnLabel,
      rejectLabel: this.cancelBtnLabel,
      message: this.deleteMsg,
      accept: () => {
        this.saving = true;
        this.saveOperatorLog(true);

        if (this._deviceService.isMobile()) {
          const tab = this._tabService.buildNewTab(
            ALL_OPLOG_SEARCH_CONTAINER,
            true
          );
          this._tabService.setMobileTab(tab);
        } else {
          const index = this._tabService.getActiveIndex();
          this._tabService.closeTab(index);
        }
      },
      reject: () => {
        this.saving = false;
      },
    });
  }

  isOwnLog(): boolean {
    return this._auth.isOwnItem(this.opLog?.createdBy);
  }

  resetFacility() {
    const detailGroup = this.form
      ?.get('OpAlertLog')
      ?.get('OpAlertLogLocation') as UntypedFormGroup;
    detailGroup.patchValue({
      locationId: null,
      state: null,
      county: null,
      township: null,
    });
    detailGroup.controls['state'].markAsDirty();
    detailGroup.controls['county'].markAsDirty();
    detailGroup.controls['township'].markAsDirty();

    const group = this.form
      ?.get('OpAlertLog')
      ?.get('OpAlertLogFacility') as UntypedFormGroup;
    group.patchValue({
      facilityName: null,
      facilityType: null,
      aor: null,
      facilityId: null,
      facilityUid: null,
    });

    group.controls['facilityName'].markAsDirty();
    group.controls['facilityType'].markAsDirty();
    group.controls['aor'].markAsDirty();
    group.controls['facilityId'].markAsDirty();
  }

  validateForm() {
    this.addEntryForm.updateValueAndValidity();
  }

  indexChanged($event: any): void {
    if (!isNullOrUndefined($event)) {
      this.tabIndex = $event.index;
    }
  }
}
